import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  useNavigate,
  useParams,
  useLocation,
  useOutletContext,
} from "react-router-dom";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CButton,
  CFormInput,
  CSpinner,
} from "@coreui/react";

import Project from "./ProjectPage";
import { apiClient } from "services/Api";
import CIcon from "@coreui/icons-react";
import {
  cilArrowLeft,
  cilBell,
  cilDescription,
  cilFile,
  cilFolderOpen,
  cilInfo,
  cilPeople,
  cilSave,
  cilWarning,
} from "@coreui/icons";
import CompanyLogo from "components/CompanyLogo";
import LoadingScreen from "components/globals/LoadingScreen";
import ToastNotification from "components/globals/ToastNotification";
import DataTableComponent from "components/DataTableComponent";
import Employees from "./modals/Employees";
import EditProject from "./modals/EditProject";
import TimesheetExportButton from "views/roberto/component/TimesheetExportButton";
import { jwtDecode } from "jwt-decode";
import { Tour } from "@reactour/tour";
import { CLIENT_TOUR_STEPS } from "./tourSteps";

const ClientPage = () => {
  const { token } = useParams();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const projetoId = query.get("projeto");

  const [toast, setToast] = useState(null);

  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | inactive | missing | error | redirecting
  const [errorInfo, setErrorInfo] = useState("");

  const [loadingPendingChangesPdf, setLoadingPendingChangesPdf] = useState(false);
  const [notifyingPendingChanges, setNotifyingPendingChanges] = useState(false);

  const [selectedProject, setSelectedProject] = useState();
  const [showEmployees, setShowEmployees] = useState(false);
  const [showProjectInfo, setShowProjectInfo] = useState(false);
  const navigate = useNavigate();

  const [handleBack, setHandleBack] = useState(null);
  const [handleSave, setHandleSave] = useState(null);
  const [saving, setSaving] = useState(false);
  const [timesheetExporter, setTimesheetExporter] = useState(null);
  const [FilterButton, setFilterButton] = useState(null);
  const [runClientTour, setRunClientTour] = useState(false);
  const [clientTourStepIndex, setClientTourStepIndex] = useState(0);
  const outletContext = useOutletContext();
  const setClientBranding = outletContext?.setClientBranding;

  const hasValidInternalSession = useMemo(() => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return false;
      const decoded = jwtDecode(token);
      if (decoded?.exp && decoded.exp * 1000 <= Date.now()) return false;
      return true;
    } catch (error) {
      console.error("Token error:", error);
      return false;
    }
  }, []);

  const handleSaveClick = useCallback(async () => {
    if (!handleSave || saving) return;
    setSaving(true);
    try {
      await handleSave();
    } finally {
      setSaving(false);
    }
  }, [handleSave, saving]);

  const renderErrorState = (title, description, icon = cilWarning) => (
    <CCard className="shadow-sm border-0">
      <CCardBody className="text-center py-5 px-4">
        <CIcon icon={icon} size="xxl" className="text-danger mb-3" />
        <h4 className="mb-0">{title}</h4>
        <p className="text-muted mb-4">{description}</p>
        <p className="text-muted mb-4">
          Para continuar, fale com a equipa da STREAM/ ou com o responsável do
          projeto para obter um novo acesso ou mais informação.
        </p>
      </CCardBody>
    </CCard>
  );

  useEffect(() => {
    setStatus("loading");
    setErrorInfo("");
    setClient(null);
    setProjects(null);

    apiClient
      .get(`/projects/management/${token}/projects/`, { skipAuth: true })
      .then((response) => {
        const data = response.data;
        setClient(data.client);
        setProjects(data.projects);
        setClientBranding?.({
          name: data.client?.name || "",
          logo: data.client?.image || "",
        });
        setStatus("ready");
      })
      .catch((error) => {
        console.error("Erro ao carregar projeto:", error);
        const statusCode = error.response?.status;
        const detail = error.response?.data?.detail || "";

        setClientBranding?.({ name: "", logo: "" });

        if (statusCode === 403) {
          setStatus("inactive");
          setErrorInfo(
            detail || "Este link público já não está ativo ou foi revogado.",
          );
          return;
        }

        if (statusCode === 404) {
          setStatus("missing");
          setErrorInfo(
            detail || "Não encontrámos o acesso público que tentou aceder.",
          );
          return;
        }

        setStatus("error");
        setErrorInfo(
          detail ||
            "Ocorreu um problema a carregar o portal do cliente. Tente novamente.",
        );
      });
    return () => {
      setClientBranding?.({ name: "", logo: "" });
    };
  }, [token, setClientBranding]);

  useEffect(() => {
    const onTokenInvalid = (event) => {
      setClientBranding?.({ name: "", logo: "" });
      setStatus("inactive");
      setErrorInfo(
        event.detail ||
          "Este link público já não está disponível. Peça novo acesso ao gestor.",
      );
    };
    window.addEventListener("client-portal-token-invalid", onTokenInvalid);
    return () => {
      window.removeEventListener("client-portal-token-invalid", onTokenInvalid);
    };
  }, [setClientBranding]);

  useEffect(() => {
    setSelectedProject(projetoId);
  }, [projetoId]);

  useEffect(() => {
    if (!selectedProject) return;
    setRunClientTour(false);
    setClientTourStepIndex(0);
  }, [selectedProject]);

  useEffect(() => {
    if (!selectedProject) {
      setFilterButton(null);
      setTimesheetExporter(null);
      setHandleSave(null);
      setHandleBack(() => null);
      return;
    }

    setHandleBack(() => () => navigate(`/projetos/gestao/token/${token}`));
  }, [selectedProject, navigate, token]);

  const triggerDownloadFromUrl = useCallback((downloadUrl) => {
    const newWindow = window.open(downloadUrl, "_blank", "noopener,noreferrer");
    if (newWindow) {
      newWindow.opener = null;
    }
  }, []);

  const waitForExportTask = useCallback(
    async (taskId) => {
      const maxAttempts = 60;
      const delayMs = 5000;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const response = await apiClient.get(
          `/projects/management/${token}/export/task/${taskId}/`,
          {
            responseType: "json",
            validateStatus: () => true,
          },
        );

        if (response.status === 202) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          continue;
        }

        if (response.status === 200 && response.data?.download_url) {
          triggerDownloadFromUrl(response.data.download_url);
          return;
        }

        throw new Error(response.data?.error || "Exportação falhou");
      }

      throw new Error("Tempo de espera esgotado");
    },
    [token, triggerDownloadFromUrl],
  );

  const handlePendingChangesPdfExport = useCallback(async () => {
    if (!selectedProject || loadingPendingChangesPdf) return;
    setLoadingPendingChangesPdf(true);

    try {
      const response = await apiClient.get(
        `/projects/management/${token}/${selectedProject}/pending_changes/`,
        {
          params: { file: "pdf" },
          responseType: "json",
          validateStatus: () => true,
        },
      );

      if (response.status === 200 && response.data?.download_url) {
        triggerDownloadFromUrl(response.data.download_url);
        return;
      }

      if (response.status === 202 && response.data?.task_id) {
        await waitForExportTask(response.data.task_id);
        return;
      }

      throw new Error(
        response.data?.detail || "Não foi possível iniciar a exportação.",
      );
    } catch (error) {
      setToast({
        title: "Alterações Pendentes",
        type: "error",
        message:
          error?.message || "Não foi possível exportar o PDF das alterações.",
      });
    } finally {
      setLoadingPendingChangesPdf(false);
    }
  }, [
    loadingPendingChangesPdf,
    selectedProject,
    setToast,
    token,
    triggerDownloadFromUrl,
    waitForExportTask,
  ]);

  const handleNotifyPendingChanges = useCallback(async () => {
    if (!selectedProject || notifyingPendingChanges) return;
    setNotifyingPendingChanges(true);

    try {
      await apiClient.post(
        `/projects/management/${token}/${selectedProject}/pending_changes_notify/`,
      );
      setToast({
        title: "Alterações Pendentes",
        type: "success",
        message: "A equipa de gestão de projeto foi notificada.",
      });
    } catch (error) {
      setToast({
        title: "Alterações Pendentes",
        type: "error",
        message:
          error?.response?.data?.detail ||
          "Não foi possível enviar a notificação.",
      });
    } finally {
      setNotifyingPendingChanges(false);
    }
  }, [notifyingPendingChanges, selectedProject, setToast, token]);

  const handleStartTour = useCallback(() => {
    setClientTourStepIndex(0);
    setRunClientTour(true);
  }, []);

  const clientTourSteps = useMemo(() => {
    if (!selectedProject) {
      return CLIENT_TOUR_STEPS.map((step) => ({
        selector: step.target,
        content: (
          <div>
            <div className="fw-semibold mb-1">{step.title}</div>
            <div>{step.content}</div>
          </div>
        ),
        position: step.placement || "bottom",
      }));
    }
    return [];
  }, [selectedProject]);

  useEffect(() => {
    if (status !== "ready" || !client || !hasValidInternalSession) return;

    setStatus("redirecting");
    const target = `/projetos/gestao/${client.id}${
      projetoId ? `?projeto=${projetoId}` : ""
    }`;
    navigate(target, { replace: true });
  }, [status, client, hasValidInternalSession, navigate, projetoId, setStatus]);

  // useEffect(() => {
  //   if (!runClientTour) return undefined;
  //   const previousOverflow = document.body.style.overflow;
  //   document.body.style.overflow = "hidden";
  //   return () => {
  //     document.body.style.overflow = previousOverflow;
  //   };
  // }, [runClientTour]);

  if (status === "loading") {
    return (
      <LoadingScreen
        message="A validar o acesso ao portal do cliente..."
        containerHeight="30vh"
      />
    );
  }

  if (status === "redirecting") {
    return (
      <LoadingScreen
        message="A encaminhar para a versão interna..."
        containerHeight="30vh"
      />
    );
  }

  if (status === "inactive") {
    return renderErrorState(
      "Link público inativo",
      "O link utilizado já não está disponível ou foi revogado.",
    );
  }

  if (status === "missing") {
    return renderErrorState(
      "Link público não encontrado",
      errorInfo || "Não encontrámos este link público.",
    );
  }

  if (status === "error") {
    return renderErrorState(
      "Não foi possível abrir o portal",
      errorInfo || "Tente novamente mais tarde.",
      cilWarning,
    );
  }

  if (!client)
    return (
      <LoadingScreen message="A carregar cliente..." containerHeight="30vh" />
    );

  return (
    <>
      {runClientTour && !selectedProject && clientTourSteps.length > 0 && (
        <Tour
          key="client-tour"
          isOpen
          setIsOpen={(open) => {
            if (typeof open !== "boolean") return;
            setRunClientTour(open);
            if (!open) setClientTourStepIndex(0);
          }}
          onClickClose={() => {
            setRunClientTour(false);
            setClientTourStepIndex(0);
          }}
          onClickMask={() => {
            setRunClientTour(false);
            setClientTourStepIndex(0);
          }}
          steps={clientTourSteps}
          currentStep={clientTourStepIndex}
          setCurrentStep={setClientTourStepIndex}
          showBadge={false}
          showNavigation
          showDots
          showPrevNextButtons
          showCloseButton
          disableInteraction={false}
          closeWithMask
        />
      )}

      {/* Toolbar */}
      <CCard
        className="client-ext-toolbar tour-client-toolbar mb-3"
        style={{
          position: "sticky",
          top: "var(--client-navbar-height, 64px)",
          zIndex: 1040,
        }}
      >
        <div className="d-flex flex-column flex-xl-row justify-content-between align-items-start align-items-xl-center gap-2">
          <div className="client-ext-toolbar-group d-flex flex-wrap align-items-center gap-2">
            <CButton
              color="secondary"
              size="sm"
              className="d-flex align-items-center"
              onClick={() => handleBack?.()}
            >
              <CIcon icon={cilArrowLeft} className="me-2" />
              Voltar
            </CButton>

            <CButton
              color="info"
              size="sm"
              className="d-flex align-items-center text-nowrap tour-client-employees-btn"
              onClick={() => setShowEmployees(true)}
            >
              <CIcon icon={cilPeople} className="me-2" />
              Funcionários
            </CButton>

            {selectedProject && (
              <CButton
                color="info"
                size="sm"
                className="d-flex align-items-center text-nowrap tour-client-project-details-btn"
                onClick={() => setShowProjectInfo(true)}
              >
                <CIcon icon={cilFile} className="me-2" />
                Detalhe do projeto
              </CButton>
            )}
            {selectedProject && (
              <CButton
                color="warning"
                size="sm"
                className="d-flex align-items-center text-nowrap"
                disabled={notifyingPendingChanges}
                onClick={handleNotifyPendingChanges}
              >
                {notifyingPendingChanges ? (
                  <CSpinner component="span" size="sm" className="me-2" />
                ) : (
                  <CIcon icon={cilBell} className="me-2" />
                )}
                Notificar STREAM
              </CButton>
            )}
            {selectedProject && (
              <CButton
                color="secondary"
                size="sm"
                className="d-flex align-items-center text-nowrap"
                disabled={loadingPendingChangesPdf}
                onClick={handlePendingChangesPdfExport}
              >
                {loadingPendingChangesPdf ? (
                  <CSpinner component="span" size="sm" className="me-2" />
                ) : (
                  <CIcon icon={cilDescription} className="me-2" />
                )}
                Exportar Alterações PDF
              </CButton>
            )}
            {selectedProject && (
              <TimesheetExportButton
                size="sm"
                management={timesheetExporter?.management}
                onExport={timesheetExporter?.run}
              />
            )}
          </div>

          <div className="client-ext-toolbar-group d-flex flex-wrap align-items-center gap-2 ms-xl-auto">
            {FilterButton && (
              <div className="order-1 order-xl-0 tour-client-filter-btn">
                {FilterButton}
              </div>
            )}
            {handleSave && (
              <CButton
                color="success"
                size="sm"
                className="d-flex align-items-center text-nowrap tour-client-save-btn"
                disabled={saving}
                onClick={handleSaveClick}
              >
                {saving ? (
                  <CSpinner component="span" size="sm" className="me-2" />
                ) : (
                  <CIcon icon={cilSave} className="me-2" />
                )}
                {saving ? "A guardar" : "Guardar"}
              </CButton>
            )}
          </div>
        </div>
      </CCard>

      <ToastNotification toast={toast} setToast={setToast} />

      {/* Header */}
      <CCard className="mb-3" id="project-details-section">
        <CCardBody className="px-5 py-4">
          <CRow className="align-items-center">
            <CCol className="text-center mr-5t" md={6}>
              {client.image ? (
                <CompanyLogo logo={client.image} maxWidth={200} />
              ) : (
                <h3 className="mb-0 fw-bold">{client.name}</h3>
              )}
            </CCol>
            <CCol md={6}>
              <CRow className="mb-2">
                <CCol>
                  <strong>Email:</strong>
                  <CFormInput
                    value={client.email || "N.A"}
                    type="text"
                    disabled
                    readOnly
                  />
                </CCol>
              </CRow>
              <CRow>
                <CCol>
                  <strong>Morada:</strong>
                  <CFormInput
                    value={client.address || "N.A"}
                    type="text"
                    disabled
                    readOnly
                  />
                </CCol>
              </CRow>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      {showEmployees && (
        <Employees
          clientId={token}
          onClose={() => setShowEmployees(false)}
          setToast={setToast}
          readOnly
        />
      )}

      {showProjectInfo && selectedProject && (
        <EditProject
          clientId={token}
          projectId={selectedProject}
          onClose={() => setShowProjectInfo(false)}
          setToast={setToast}
          readOnly
        />
      )}

      {!selectedProject && (
        <CCard className="tour-client-projects-table">
          <CCardHeader>
            <div className="d-flex align-items-center justify-content-between gap-2">
              <strong>Projetos do Cliente</strong>
              <CButton
                color="primary"
                variant="ghost"
                size="sm"
                className="d-inline-flex align-items-center gap-1 text-nowrap px-2"
                onClick={handleStartTour}
              >
                <CIcon icon={cilInfo} />
                Tutorial
              </CButton>
            </div>
          </CCardHeader>
          <CCardBody>
            <DataTableComponent
              columns={[
                {
                  name: "Nome do Projeto",
                  selector: (d) => (
                    <strong
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(`/projetos/gestao/token/${token}?projeto=${d.id}`)
                      }
                    >
                      {d.name}
                    </strong>
                  ),
                  center: true,
                },
                {
                  name: "Data de Início",
                  selector: (d) => d.management.start_date,
                  center: true,
                },
                {
                  name: "Data de Conclusão",
                  selector: (d) => d.management.end_date,
                  center: true,
                },
                {
                  name: "Executado Até",
                  selector: (d) => d.management.executed || "N.A",
                  center: true,
                },
                {
                  name: "Entidade Responsável",
                  selector: (d) => d.management.responsible_entity,
                  center: true,
                },
                {
                  name: "Programa",
                  selector: (d) => d.program,
                  center: true,
                },
              ]}
              data={projects || []}
              // onRowClicked={(row, event) => setSelectedProject(row.id)}
              onRowClicked={(row) =>
                navigate(`/projetos/gestao/token/${token}?projeto=${row.id}`)
              }
              noData={
                <CCard
                  className="border-0"
                  style={{
                    background: "#f5f6fa",
                    width: "100%",
                    minHeight: "220px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CCardBody className="w-100 d-flex flex-column align-items-center justify-content-center text-center">
                    <CIcon
                      icon={cilFolderOpen}
                      size="xxl"
                      className="mb-3 text-muted"
                    />
                    <div className="h5 text-muted mb-2">
                      Nenhum projeto encontrado
                    </div>
                    <div className="text-muted small">
                      Não existem projetos a apresentar neste momento.
                    </div>
                  </CCardBody>
                </CCard>
              }
            />
          </CCardBody>
        </CCard>
      )}

      {selectedProject && (
        <Project
          clientId={token}
          projectId={selectedProject}
          setToast={setToast}
          setFilterButton={setFilterButton}
          setHandleSave={setHandleSave}
          setTimesheetExporter={setTimesheetExporter}
        />
      )}
    </>
  );
};

export default ClientPage;
